import { normaliseVariantName } from '@utils/frames';
import type { TFunction } from 'next-i18next';

import type { FrameOptionGroup } from '../../../../design-system/components/Framing/types';
import type { FrameOptionConsolidated } from '../../../../types/Products';
import type { FrameDraft } from '../hooks/useFrameDraft';
import { getStylePrice } from './framePricing';

const MOUNTING_THUMBNAILS: Record<string, string> = {
  'float-mount': '/images/float-mount-thumbnail.jpg',
  'window-float-mount': '/images/window-float-mount-thumbnail.jpg',
  'window-mount': '/images/window-mount-thumbnail.jpg',
};

/** Short spec lines under an option, as on the designs; none if unknown. */
const getDetails = (t: TFunction, key: string): string[] | undefined => {
  const value = t(key, { returnObjects: true }) as unknown;

  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : undefined;
};

const glazingDetailsKey = (glazing: string) => {
  if (/museum/i.test(glazing)) return 'museum';
  return /uv/i.test(glazing) ? 'standard' : glazing;
};

/**
 * Maps a product's frame options and the in-progress draft to the option
 * groups `FrameDrawer` renders. Price deltas come from the shared pricing
 * helpers, so the previewer and checkout agree on every number.
 */
export const buildFrameGroups = (
  t: TFunction,
  frameOptions: FrameOptionConsolidated[],
  draft: FrameDraft,
): FrameOptionGroup[] => {
  const selectedStyle =
    frameOptions.find(({ title }) => title === draft.selectedStyleTitle) ??
    null;
  const selectedStylePrice = getStylePrice(selectedStyle);

  return [
    {
      id: 'frame',
      title: t('groups.frame'),
      selectedId: draft.selectedStyleTitle,
      options: frameOptions.map((option) => ({
        id: option.title,
        name: option.title,
        description: option.description,
        thumbnail: option.images[0],
        priceDelta: getStylePrice(option) - selectedStylePrice,
        badge: option.recommended ? t('artistsChoice') : null,
      })),
    },
    {
      id: 'mounting',
      title: t('groups.mounting'),
      selectedId: draft.selectedMounting,
      options:
        draft.mountingOptions.length > 1
          ? draft.mountingOptions.map((option) => ({
              id: option.mounting,
              name: option.mounting,
              features: getDetails(
                t,
                `optionDetails.mounting.${normaliseVariantName(option.mounting)}`,
              ),
              thumbnail: {
                src:
                  option.image ??
                  MOUNTING_THUMBNAILS[normaliseVariantName(option.mounting)],
                alt: option.mounting,
              },
              priceDelta: option.priceDelta,
            }))
          : [],
    },
    {
      id: 'glazing',
      title: t('groups.glazing'),
      selectedId: draft.selectedGlazing,
      // One glazing (or none, as "Default Title") is nothing to choose.
      options:
        draft.glazingOptions.length > 1
          ? draft.glazingOptions.map((option) => ({
              id: option.glazing,
              name: option.glazing,
              features: getDetails(
                t,
                `optionDetails.glazing.${glazingDetailsKey(option.glazing)}`,
              ),
              priceDelta: option.priceDelta,
            }))
          : [],
    },
  ];
};

/** Routes a `FrameDrawer` selection to the matching draft setter. */
export const selectFrameGroupOption = (
  draft: FrameDraft,
  groupId: string,
  optionId: string,
) => {
  if (groupId === 'frame') draft.selectStyle(optionId);
  if (groupId === 'mounting') draft.selectMounting(optionId);
  if (groupId === 'glazing') draft.selectGlazing(optionId);
};
